<template>
    <vx-modal v-model:visible="visible" size="md" padding="standard">
        <template v-slot:header>Turn off Require SSO</template>

        <div class="flex flex-col gap-4 text-sm text-slate-800">
            <div>
                Turning off Require SSO lets every member of this team log in with a password again. Use this only when
                the customer's identity provider is down or misconfigured and the owner cannot fix it. This is recorded
                in the team's audit log with your name and reason.
            </div>

            <div>
                <vx-label size="md">Reason</vx-label>
                <vx-input v-model="reason" size="lg" :rows="3" maxlength="250" data-test="reason"></vx-input>
                <vx-char-counter :content="reason" :max="250" />
            </div>
        </div>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 lg:flex-row">
                <vx-button
                    @click="visible = false"
                    type="button"
                    class="grow lg:w-20 lg:grow-0"
                    size="lg"
                    color="muted"
                >
                    Cancel
                </vx-button>
                <vx-button
                    @click="confirm()"
                    data-test="confirm"
                    :disabled="reason.trim() === ''"
                    :loading="submitting"
                    type="button"
                    class="grow"
                    size="lg"
                    color="danger"
                >
                    Turn off Require SSO <font-awesome-icon widthAuto :icon="faUnlock" />
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script lang="ts" setup>
import { faUnlock } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxButton, VxCharCounter, VxInput, VxLabel, VxModal } from '@voxie/frontend-components';
import { ref, watch } from 'vue';
import { useToasted } from '~/composables/useToasted';
import { adminSsoClient } from '~/services';
import { catchRequestError } from '~/utils/request';

const props = defineProps<{ teamId: number }>();
const emit = defineEmits<{ disabled: [] }>();
const visible = defineModel<boolean>('visible', { default: false });

const toasted = useToasted();

const reason = ref('');
const submitting = ref(false);

watch(visible, (open) => {
    if (open) {
        reason.value = '';
    }
});

const confirm = async () => {
    if (submitting.value || reason.value.trim() === '') {
        return;
    }

    submitting.value = true;

    try {
        await adminSsoClient.disableRequireSso(props.teamId, { reason: reason.value.trim() });
        toasted.success('Require SSO turned off.');
        emit('disabled');
        visible.value = false;
    } catch (e) {
        toasted.error(catchRequestError(e));
    } finally {
        submitting.value = false;
    }
};
</script>
